import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import "../index.css";
import mirrorGif from "../assets/gif_mirror.gif";

const MODE = { candidate: "candidate", hiring: "hiring" };

function FileIcon({ size = 16, className = "" }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7z" />
      <path d="M14 2v5h5" />
    </svg>
  );
}

function getQS() {
  const p = new URLSearchParams(window.location.search);
  return {
    mode: p.get("mode"),
    data: p.get("data"),
    extCandidateId: p.get("external_candidate_id") || p.get("candidate_id"),
    extRecruiterId: p.get("external_recruiter_id") || p.get("recruiter_id"),
    source: p.get("source"),
    skipCv: p.get("skipCv") === "1",
  };
}

function decodeStartData(qsData) {
  if (!qsData) return null;
  try {
    const json = atob(qsData.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function normalizeText(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

const SEMANTIC_SYNONYMS = {
  common: {
    sala: ["servizio", "tavoli", "cameriere", "waiter"],
    cucina: ["chef", "cuoco", "kitchen"],
    reception: ["front desk", "accoglienza", "receptionist"],
    bar: ["barista", "cocktail", "barman", "bartender"],
    pulizie: ["housekeeping", "camere", "riassetto"],
    manager: ["responsabile", "coordinatore", "direttore"],
  },
  skills: {
    caffetteria: ["espresso", "macchina caffe", "latte art"],
    mixology: ["cocktail", "bar", "bartender"],
    haccp: ["igiene", "sicurezza alimentare"],
    vendita: ["upselling", "cross selling", "consulenza"],
    accoglienza: ["front office", "customer care", "guest relation"],
  },
};

function expandQueryTokens(query, field) {
  const base = normalizeText(query);
  if (!base) return [];
  const tokens = base.split(" ").filter(Boolean);
  const expanded = [...tokens];
  const maps = [SEMANTIC_SYNONYMS.common];
  if (field === "skills") maps.push(SEMANTIC_SYNONYMS.skills);
  maps.forEach((group) => {
    Object.entries(group).forEach(([key, values]) => {
      if (tokens.includes(key) || values.some((v) => base.includes(normalizeText(v)))) {
        expanded.push(key, ...values.map((v) => normalizeText(v)));
      }
    });
  });
  return Array.from(new Set(expanded));
}

function scoreOption(option, query, field) {
  const cleanOption = normalizeText(option);
  const cleanQuery = normalizeText(query);
  if (!cleanQuery) return 0;
  if (cleanOption === cleanQuery) return 100;
  if (cleanOption.includes(cleanQuery)) return 75;

  const queryTokens = expandQueryTokens(cleanQuery, field);
  const optionTokens = cleanOption.split(" ").filter(Boolean);
  const tokenHits = queryTokens.filter((tok) => optionTokens.some((ot) => ot.includes(tok) || tok.includes(ot)));
  const overlap = tokenHits.length / Math.max(1, queryTokens.length);

  const optionHasSemanticAlias = queryTokens.some((tok) => cleanOption.includes(tok));
  if (optionHasSemanticAlias) return 55 + overlap * 20;
  return overlap * 40;
}

export default function ChatWidget() {
  const [messages, setMessages] = useState([]);
  const [mode, setMode] = useState(null);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [privacySubmitted, setPrivacySubmitted] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const [text, setText] = useState("");
  const [pendingFile, setPendingFile] = useState(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [currentField, setCurrentField] = useState(null);
  const [selector, setSelector] = useState(null);
  const [selectorValue, setSelectorValue] = useState("");
  const [selectorValues, setSelectorValues] = useState([]);
  const [selectorQuery, setSelectorQuery] = useState("");
  const [selectorVisibleCount, setSelectorVisibleCount] = useState(18);
  const [selectorArchive, setSelectorArchive] = useState([]);
  const [editingSelectorId, setEditingSelectorId] = useState(null);
  const [currentWidget, setCurrentWidget] = useState(null);
  const [data, setData] = useState({});
  const [isThinking, setIsThinking] = useState(false);

  const endRef = useRef(null);
  const didInit = useRef(false);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const dragDepth = useRef(0);

  useEffect(() => {
    queueMicrotask(() => endRef.current?.scrollIntoView({ behavior: "smooth" }));
  }, [messages, isThinking, showIntro]);

  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;

    const qs = getQS();
    if (qs.mode && (qs.mode === MODE.candidate || qs.mode === MODE.hiring)) {
      setMode(qs.mode);
      if (qs.data) {
        startChatInMode(qs.mode);
      }
    }
  }, []);

  useEffect(() => {
    if (showIntro || isThinking) return;
    queueMicrotask(() => inputRef.current?.focus());
  }, [showIntro, isThinking]);

  const pushBot = (html) => setMessages((m) => [...m, { from: "bot", html }]);
  const pushBotMulti = (payload) => {
    if (!payload) return;
    if (Array.isArray(payload)) {
      payload.forEach((item) => {
        if (item) pushBot(item);
      });
      return;
    }
    pushBot(payload);
  };
  const pushUserText = (t) => setMessages((m) => [...m, { from: "user", text: t }]);
  const pushUserFile = (file) =>
    setMessages((m) => [...m, { from: "user", kind: "file", fileName: file.name }]);
  const isSelectorActive = () =>
    Boolean(
      selector &&
        selector.enabled &&
        Array.isArray(selector.options) &&
        selector.options.length > 0
    );
  const canSendMessage = () => {
    if (pendingFile) return true;
    if (isSelectorActive()) return false;
    return text.trim().length > 0;
  };
  const getSelectorTitle = (sel) => {
    if (!sel) return "Seleziona da elenco";
    if ((sel.field === "application_response" || sel.field === "questionario_mirror") && sel.question) return sel.question;
    if (sel.field === "desired_positions" || sel.field === "role_title") return "Seleziona posizioni";
    if (sel.field === "skills" || sel.field === "must_have_skills") return "Seleziona competenze";
    return "Seleziona da elenco";
  };
  const getFilteredSelectorOptions = () => {
    if (!isSelectorActive()) return [];
    const options = selector.options || [];
    const query = selectorQuery.trim();
    if (!query) return options;
    return options
      .map((opt) => ({ opt, score: scoreOption(opt, query, selector.field) }))
      .filter((item) => item.score >= 12)
      .sort((a, b) => b.score - a.score || a.opt.localeCompare(b.opt))
      .map((item) => item.opt);
  };
  const filteredSelectorOptions = getFilteredSelectorOptions();
  const visibleSelectorOptions = filteredSelectorOptions.slice(0, selectorVisibleCount);
  const hasMoreSelectorOptions = filteredSelectorOptions.length > selectorVisibleCount;
  const archiveSelector = (selectedValues) => {
    if (!selector || !selector.field) return;
    const normalized = selectedValues.filter((item) => typeof item === "string" && item.trim());
    if (!normalized.length) return;
    const entryId =
      editingSelectorId || `${selector.field}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    setSelectorArchive((prev) => {
      if (editingSelectorId) {
        return prev.map((item) =>
          item.id === editingSelectorId
            ? {
                ...item,
                selected: normalized,
                selector: {
                  ...item.selector,
                  selected: normalized,
                },
              }
            : item
        );
      }
      return [
        ...prev,
        {
          id: entryId,
          field: selector.field,
          title: getSelectorTitle(selector),
          // Il widget va memorizzato: senza, riaprendo la card in modifica il
          // render ricade sul selector generico (titolo in plain text -> i <br/>
          // restano visibili, e compare la ricerca "ruolo o competenza").
          widget: currentWidget,
          selected: normalized,
          selector: {
            ...selector,
            selected: normalized,
          },
        },
      ];
    });
    if (!editingSelectorId) {
      setMessages((prev) => [...prev, { from: "bot", kind: "selector_archive", archiveId: entryId }]);
    }
    setEditingSelectorId(null);
  };
  const editArchivedSelector = (entry) => {
    if (!entry?.selector) return;
    const sel = { ...entry.selector, enabled: true };
    setSelector(sel);
    setCurrentField(entry.field || sel.field || null);
    setCurrentWidget(
      entry.widget ??
        (entry.field === "questionario_mirror" || entry.field === "application_response"
          ? "simple_buttons"
          : null)
    );
    if (sel.multi) {
      setSelectorValues(Array.isArray(entry.selected) ? entry.selected : []);
      setSelectorValue("");
    } else {
      setSelectorValue(Array.isArray(entry.selected) ? entry.selected[0] || "" : "");
      setSelectorValues([]);
    }
    setSelectorQuery("");
    setSelectorVisibleCount(18);
    setEditingSelectorId(entry.id);
    queueMicrotask(() => endRef.current?.scrollIntoView({ behavior: "smooth" }));
  };
  const submitOption = async (option) => {
    if (!mode || isThinking) return;
    const sel = selector;
    if (!sel?.field) return;
    archiveSelector([option]);
    setIsThinking(true);
    try {
      const res = await axios.post("/api/answer", {
        mode,
        field: sel.field || currentField,
        value: option,
        data,
        input_method: "selector",
        selected_values: [option],
      });
      if (res.data.redirect_url) {
        setSelector(null);
        setCurrentWidget(null);
        if (res.data.question && res.data?.widget !== "simple_buttons") pushBotMulti(res.data.question);
        else if (!res.data.question) pushBotMulti("Operazione completata. Sarai reindirizzato…");
        setTimeout(() => { window.location.href = res.data.redirect_url; }, res.data.redirect_delay_ms || 3000);
      } else {
        applyServerState(res.data);
        if (res.data?.question && res.data?.widget !== "simple_buttons") pushBotMulti(res.data.question);
      }
    } catch {
      pushBotMulti("Errore di rete. Riprova.");
    } finally {
      setIsThinking(false);
      queueMicrotask(() => inputRef.current?.focus());
    }
  };

  const submitSelectorSelection = async () => {
    if (!mode || isThinking || !isSelectorActive()) return;
    const selected = selector.multi ? selectorValues : selectorValue ? [selectorValue] : [];
    const clean = selected.join(", ").trim();
    if (!clean) return;

    const wasEditing = Boolean(editingSelectorId);
    archiveSelector(selected);
    setIsThinking(true);
    try {
      const payload = {
        mode,
        field: selector.field || currentField,
        value: clean,
        data,
        input_method: "selector",
        selected_values: selected,
      };
      const res = await axios.post("/api/answer", payload);
      if (res.data.redirect_url) {
        setSelector(null);
        setCurrentWidget(null);
        pushBotMulti(res.data.question || "Operazione completata. Sarai reindirizzato…");
        setTimeout(() => { window.location.href = res.data.redirect_url; }, res.data.redirect_delay_ms || 3000);
      } else {
        const editedField = payload.field;
        if (wasEditing && (editedField === "skills" || editedField === "must_have_skills" || editedField === "nice_to_have_skills")) {
          setMessages((prev) => {
            const next = [...prev];
            const isSkillImportancePrompt = (msg) => {
              const text = String(msg?.html || msg?.text || "").toLowerCase();
              return text.includes("quanto pesa la competenza");
            };
            while (next.length > 0) {
              const last = next[next.length - 1];
              if (last?.from === "bot" && isSkillImportancePrompt(last)) {
                next.pop();
                continue;
              }
              break;
            }
            return next;
          });
        }
        applyServerState(res.data);
        const nextQuestion = res.data?.question || "";
        if (!nextQuestion) return;
        if (res.data?.widget === "simple_buttons") return;
        if (!wasEditing) {
          pushBotMulti(nextQuestion);
        } else {
          // In edit mode mostriamo la nuova domanda solo se è diversa dall'ultima già in chat.
          setMessages((prev) => {
            const lastBot = [...prev].reverse().find((m) => m.from === "bot");
            const lastText = (lastBot?.html || lastBot?.text || "").trim();
            if (lastText === String(nextQuestion).trim()) return prev;
            return [...prev, { from: "bot", html: nextQuestion }];
          });
        }
      }
    } catch {
      pushBot("Si è verificato un errore. Puoi riprovare?");
    } finally {
      setIsThinking(false);
    }
  };
  const applyServerState = (payload) => {
    setData(payload?.data || {});
    setCurrentField(payload?.field ?? null);
    setCurrentWidget(payload?.widget ?? null);
    const nextSelector = payload?.selector ?? null;
    setSelector(nextSelector);
    if (
      nextSelector &&
      nextSelector.enabled &&
      Array.isArray(nextSelector.options) &&
      nextSelector.options.length > 0
    ) {
      const selected = Array.isArray(nextSelector.selected)
        ? nextSelector.selected.filter((item) => typeof item === "string" && item.trim())
        : [];
      if (nextSelector.multi) {
        setSelectorValues(selected);
        setSelectorValue("");
      } else {
        setSelectorValue(selected[0] || "");
        setSelectorValues([]);
      }
      setSelectorQuery("");
      setSelectorVisibleCount(18);
      setText("");
      return;
    }
    setSelectorValue("");
    setSelectorValues([]);
    setSelectorQuery("");
    setSelectorVisibleCount(18);
  };
  const toggleSelectorOption = (option) => {
    if (!isSelectorActive()) return;
    if (!selector.multi) {
      setSelectorValue(option);
      return;
    }
    setSelectorValues((prev) => {
      const exists = prev.includes(option);
      if (exists) return prev.filter((item) => item !== option);
      const max = Number(selector.max_selections || 0);
      if (max > 0 && prev.length >= max) return prev;
      return [...prev, option];
    });
  };

  const startChatInMode = async (chosenMode) => {
    setIsThinking(true);
    setShowIntro(false);
    pushUserText(chosenMode === MODE.candidate ? "Avvio chat candidato" : "Avvio chat azienda");

    try {
      const qs = getQS();
      const preload = decodeStartData(qs.data) || {};
      if (qs.extCandidateId) preload.candidate_id = qs.extCandidateId;
      if (qs.extRecruiterId) preload.recruiter_id = qs.extRecruiterId;
      if (qs.source) preload.source = qs.source;
      if (qs.skipCv && chosenMode === MODE.candidate) preload.__needs_cv = false;
      preload.privacy_consent = true;

      const res = await axios.post(`/api/start?mode=${chosenMode}`, { data: preload });
      applyServerState(res.data);
      if (res.data.question && res.data.widget !== "simple_buttons") pushBotMulti(res.data.question);
    } catch {
      pushBot("Non riesco ad avviare il flusso. Riprova tra poco.");
    } finally {
      setIsThinking(false);
      queueMicrotask(() => inputRef.current?.focus());
    }
  };

  useEffect(() => {
    if (privacySubmitted && mode) {
      startChatInMode(mode);
    }
  }, [privacySubmitted]);

  const pickMode = (picked) => {
    setMode(picked);
    startChatInMode(picked);
  };

  const doSend = async () => {
    if (!mode || isThinking) return;

    if (pendingFile) {
      pushUserFile(pendingFile);
      setIsThinking(true);
      try {
        const form = new FormData();
        form.append("file", pendingFile);
        form.append("data", JSON.stringify(data));
        const res = await axios.post(`/api/upload?mode=${mode}`, form);
        applyServerState(res.data);
        setPendingFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        setText("");
        pushBotMulti(res.data.question || "File ricevuto.");
      } catch (err) {
        pushBot("Caricamento non riuscito. Riprova.");
      } finally {
        setIsThinking(false);
      }
      return;
    }

    const clean = text.trim();
    if (!clean) return;

    pushUserText(clean);
    setText("");
    setIsThinking(true);
    try {
      const payload = {
        mode,
        field: currentField,
        value: clean,
        data,
        input_method: "text",
        selected_values: [],
      };
      const res = await axios.post("/api/answer", payload);
      if (res.data.redirect_url) {
        setSelector(null);
        setCurrentWidget(null);
        pushBotMulti(res.data.question || "Operazione completata. Sarai reindirizzato…");
        setTimeout(() => { window.location.href = res.data.redirect_url; }, res.data.redirect_delay_ms || 3000);
      } else {
        applyServerState(res.data);
        if (res.data?.question && res.data?.widget !== "simple_buttons") pushBotMulti(res.data.question);
      }
    } catch {
      pushBot("Si è verificato un errore. Puoi riprovare?");
    } finally {
      setIsThinking(false);
    }
  };

  const handleKeyDown = (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
      if (isSelectorActive()) return;
      if (canSendMessage()) doSend();
    }
  };

  const isFileDragEvent = (e) =>
    Array.from(e.dataTransfer?.types || []).includes("Files");

  const handleDragEnter = (e) => {
    if (!isFileDragEvent(e)) return;
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current += 1;
    setIsDragActive(true);
  };

  const handleDragOver = (e) => {
    if (!isFileDragEvent(e)) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    if (!isFileDragEvent(e)) return;
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current -= 1;
    if (dragDepth.current <= 0) {
      dragDepth.current = 0;
      setIsDragActive(false);
    }
  };

  const handleDrop = (e) => {
    if (!isFileDragEvent(e)) return;
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current = 0;
    setIsDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) setPendingFile(file);
  };

  const clearPendingFile = () => {
    setPendingFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const Intro = () => (
    <div className="message bot">
      <div className="bubble bot">
        <div className="intro-form">
          {!privacySubmitted ? (
            <>
              <div><strong>Benvenuto!</strong> Per iniziare, accetta la nostra informativa sulla privacy.</div>
              <div className="intro-check">
                <input
                  type="checkbox"
                  id="privacy"
                  checked={privacyChecked}
                  onChange={(e) => setPrivacyChecked(e.target.checked)}
                />
                <label htmlFor="privacy" style={{ fontSize: '0.9rem' }}>
                  Ho letto e accetto l'<a href="/privacy.html" target="_blank" rel="noopener noreferrer">informativa privacy</a>.
                </label>
              </div>
              <div className="intro-actions">
                <button
                  className="btn primary"
                  onClick={() => setPrivacySubmitted(true)}
                  disabled={!privacyChecked}
                >
                  Accetta e Continua
                </button>
              </div>
            </>
          ) : (
            !mode && (
              <>
                <div>Scegli il tipo di chat per iniziare.</div>
                <div className="intro-actions">
                  <button className="btn primary" onClick={() => pickMode(MODE.candidate)}>
                    Sono un candidato
                  </button>
                  <button className="btn" onClick={() => pickMode(MODE.hiring)}>
                    Sono un’azienda
                  </button>
                </div>
              </>
            )
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={`chatwidget ${isDragActive ? "drag-active" : ""}`}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="messages full">
        {showIntro && <Intro />}
        {messages.map((m, i) => (
          <div key={i} className={`message ${m.from}`}>
            <div className={`bubble ${m.from}`}>
              {m.html ? (
                <div dangerouslySetInnerHTML={{ __html: m.html }} />
              ) : m.kind === "selector_archive" ? (
                (() => {
                  const entry = selectorArchive.find((item) => item.id === m.archiveId);
                  if (!entry) return null;
                  // simple_buttons in editing: mostra il widget inline al posto del card
                  const isSimpleButtonsField = entry.field === "application_response" || entry.field === "questionario_mirror";
                  if (entry.id === editingSelectorId && isSimpleButtonsField && selector) {
                    return (
                      <>
                        {selector.question && (
                          <div dangerouslySetInnerHTML={{__html: selector.question}} style={{marginBottom:8, lineHeight:1.5}} />
                        )}
                        <div className="selector-options">
                          {(selector.options || []).map((option) => (
                            <button
                              key={option}
                              type="button"
                              className={`selector-chip${entry.selected.includes(option) ? " active" : ""}`}
                              onClick={() => submitOption(option)}
                              disabled={isThinking}
                            >
                              {option}
                            </button>
                          ))}
                        </div>
                      </>
                    );
                  }
                  // Modifica disabilitata non appena si è andati oltre il campo
                  // (flow candidatura e domanda Mirror: rieditarli dopo l'invio
                  // rilancerebbe la submission).
                  const lockAfterStep = entry.field === "application_response" || entry.field === "questionario_mirror";
                  const appFlowDone = lockAfterStep && currentField !== entry.field;
                  return (
                    <div className="selector-card archived">
                      <div className="selector-title" dangerouslySetInnerHTML={{__html: entry.title}} />
                      <div className="selector-options">
                        {entry.selected.map((option) => (
                          <span key={`${entry.id}-${option}`} className="selector-chip active static">
                            {option}
                          </span>
                        ))}
                      </div>
                      {!appFlowDone && (
                        <div className="selector-actions">
                          <button
                            type="button"
                            className="selector-confirm alt"
                            onClick={() => editArchivedSelector(entry)}
                            disabled={isThinking}
                          >
                            Modifica
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })()
              ) : m.kind === "file" ? (
                <div className="filemsg" title={m.fileName}>
                  <FileIcon className="filemsg-icon" />
                  <span className="filemsg-name">{m.fileName}</span>
                </div>
              ) : (
                m.text
              )}
            </div>
          </div>
        ))}
        {isSelectorActive() && !showIntro && currentWidget === "mirror" && (
          <div className="message bot">
            <div className="bubble bot mirror-card" style={{flexDirection:"column", alignItems:"center", gap:12}}>
              <img src={mirrorGif} alt="Mirror" className="mirror-gif" />
              <div className="selector-options" style={{flexDirection:"column", gap:8, width:"100%"}}>
                {(selector.options || []).map((option) => (
                  <button
                    key={option}
                    type="button"
                    className="btn"
                    style={{width:"100%", padding:"10px 16px", fontSize:"0.95rem"}}
                    onClick={() => submitOption(option)}
                    disabled={isThinking}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
        {isSelectorActive() && !showIntro && currentWidget === "simple_buttons" && !editingSelectorId && !isThinking && (
          <div className="message bot">
            <div className="bubble bot mirror-card" style={{flexDirection:"column", alignItems:"center", gap:10}}>
              {selector.field === "questionario_mirror" && (
                <img src={mirrorGif} alt="Mirror" className="mirror-gif" />
              )}
              {selector.question && (
                <div dangerouslySetInnerHTML={{__html: selector.question}} style={{marginBottom:4, lineHeight:1.5, alignSelf:"flex-start"}} />
              )}
              <div className="selector-options">
                {(selector.options || []).map((option) => (
                  <button
                    key={option}
                    type="button"
                    className="selector-chip"
                    onClick={() => submitOption(option)}
                    disabled={isThinking}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
        {isSelectorActive() && !showIntro && currentWidget !== "mirror" && currentWidget !== "simple_buttons" && (
          <div className="message bot">
            <div className="bubble bot selector-card">
              <div className="selector-title">{getSelectorTitle(selector)}</div>
              <input
                className="selector-search"
                type="text"
                value={selectorQuery}
                onChange={(e) => {
                  setSelectorQuery(e.target.value);
                  setSelectorVisibleCount(18);
                }}
                placeholder="Cerca ruolo o competenza…"
                disabled={isThinking}
              />
              <div className="selector-options">
                {visibleSelectorOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={`selector-chip ${(selector.multi ? selectorValues.includes(option) : selectorValue === option) ? "active" : ""}`}
                    onClick={() => toggleSelectorOption(option)}
                    disabled={isThinking}
                  >
                    {option}
                  </button>
                ))}
              </div>
              {hasMoreSelectorOptions && (
                <button
                  type="button"
                  className="selector-more"
                  onClick={() => setSelectorVisibleCount((n) => n + 18)}
                  disabled={isThinking}
                  title="Mostra altre opzioni"
                >
                  <span className="dots">...</span>
                  <span>Altre ({filteredSelectorOptions.length - selectorVisibleCount})</span>
                </button>
              )}
              {selector.multi && (
                <div className="selector-meta">
                  Selezionati: {selectorValues.length}
                  {selector.max_selections ? ` / ${selector.max_selections}` : ""}
                </div>
              )}
              {filteredSelectorOptions.length === 0 && (
                <div className="selector-empty">Nessun risultato simile. Prova con un termine diverso.</div>
              )}
              <div className="selector-actions">
                <button
                  type="button"
                  className="selector-confirm"
                  onClick={submitSelectorSelection}
                  disabled={
                    isThinking ||
                    (selector.multi ? selectorValues.length === 0 : !selectorValue)
                  }
                >
                  Conferma
                </button>
              </div>
            </div>
          </div>
        )}
        {isThinking && (
          <div className="message bot">
            <div className="bubble bot">
              <span className="typing"><span className="dot"></span><span className="dot"></span><span className="dot"></span></span>
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>
      <footer className="inputbar full">
        <div className="inputwrap">
            <label className="upload" title="Carica file">
              +
              <input
                type="file"
                accept=".pdf,.doc,.docx,.txt,.md,image/*"
                ref={fileInputRef}
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  if (file) setPendingFile(file);
                  e.target.value = "";
                }}
              />
            </label>
            {pendingFile && (
              <span className="filechip" title={pendingFile.name}>
                <FileIcon className="filechip-icon" />
                <span className="filechip-name">{pendingFile.name}</span>
                <button
                  type="button"
                  className="filechip-remove"
                  onClick={clearPendingFile}
                  aria-label="Rimuovi file"
                  title="Rimuovi"
                >
                  ×
                </button>
              </span>
            )}
          {isSelectorActive() ? (
            <input
              className="input"
              type="text"
              value=""
              placeholder="Conferma dall'elenco qui sopra…"
              readOnly
              disabled
            />
          ) : (
            <input
              className="input"
              ref={inputRef}
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Scrivi un messaggio…"
              disabled={isThinking || showIntro}
            />
          )}
          <button className="send" onClick={doSend} disabled={isThinking || isSelectorActive() || !canSendMessage()}>
            ➤
          </button>
        </div>
      </footer>
    </div>
  );
}
